// portal/src/components/timeline/TimelineCollage.tsx

"use client";

import { useMemo, useState } from "react";
import { useTranslations, useFormatter } from "next-intl";
import TimelineMedia from "./TimelineMedia";
import type { TimelinePostItem } from "@/lib/timeline";

type Props = {
  items: TimelinePostItem[];
};

type MediaKind = "image" | "video" | "audio" | "file";

function getKind(item: TimelinePostItem): MediaKind {
  return item.file.media_kind as MediaKind;
}

function groupItems(items: TimelinePostItem[]) {
  const images = items.filter((item) => getKind(item) === "image");
  const videos = items.filter((item) => getKind(item) === "video");
  const audios = items.filter((item) => getKind(item) === "audio");
  const files = items.filter((item) => getKind(item) === "file");

  return {
    images,
    videos,
    audios,
    files,
    visuals: [...images, ...videos],
  };
}

function MediaCountPill({ label, count }: { label: string; count: number }) {
  const format = useFormatter();
  if (!count) return null;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        borderRadius: 999,
        padding: "0.3rem 0.55rem",
        fontSize: "0.72rem",
        fontWeight: 700,
        background: "color-mix(in srgb, currentColor 7%, transparent)",
        border: "1px solid color-mix(in srgb, currentColor 9%, transparent)",
        opacity: 0.9,
      }}
    >
      {label} {format.number(count)}
    </span>
  );
}

function SectionTitle({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          fontSize: "0.86rem",
          fontWeight: 750,
          opacity: 0.84,
        }}
      >
        {children}
      </div>

      {action}
    </div>
  );
}

function VisualOverflowLabel({
  hiddenItems,
}: {
  hiddenItems: TimelinePostItem[];
}) {
  const counts = hiddenItems.reduce(
    (acc, item) => {
      const kind = getKind(item);
      acc[kind] += 1;
      return acc;
    },
    {
      image: 0,
      video: 0,
      audio: 0,
      file: 0,
    } satisfies Record<MediaKind, number>
  );

  const t = useTranslations("timelineCollage");
  const format = useFormatter();

  const summary = [
    counts.image ? t("imageCount", { count: counts.image }) : null,
    counts.video ? t("videoCount", { count: counts.video }) : null,
    counts.audio ? t("audioCount", { count: counts.audio }) : null,
    counts.file ? t("fileCount", { count: counts.file }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.58)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        fontWeight: 800,
        borderRadius: 14,
        backdropFilter: "blur(4px)",
        pointerEvents: "none",
        textAlign: "center",
        padding: 12,
      }}
    >
      <div style={{ fontSize: "2rem", lineHeight: 1 }}>
        +{format.number(hiddenItems.length)}
      </div>

      {summary ? (
        <div
          style={{
            fontSize: "0.75rem",
            lineHeight: 1.35,
            opacity: 0.88,
          }}
        >
          {summary}
        </div>
      ) : null}
    </div>
  );
}

function VisualGrid({ items }: { items: TimelinePostItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const t = useTranslations("timelineCollage");

  if (items.length === 0) return null;

  const visibleItems = expanded ? items : items.slice(0, 4);
  const hiddenItems = expanded ? [] : items.slice(4);
  const hiddenCount = hiddenItems.length;

  if (items.length === 1) {
    return (
      <div>
        <TimelineMedia file={items[0].file} />
      </div>
    );
  }

  const isThreeLayout = visibleItems.length === 3;

  return (
    <section>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isThreeLayout ? "1.2fr 0.8fr" : "1fr 1fr",
          gap: 6,
        }}
      >
        {visibleItems.map((item, index) => {
          const isLargeThreeItem = isThreeLayout && index === 0;

          return (
            <div
              key={item.id}
              style={{
                position: "relative",
                display: "block",
                width: "100%",
                height: isLargeThreeItem ? 366 : 180,
                padding: 0,
                border: "none",
                background: "transparent",
                cursor: hiddenCount > 0 && index === 3 ? "pointer" : "default",
                overflow: "hidden",
                borderRadius: 14,
                gridRow: isLargeThreeItem ? "span 2" : undefined,
              }}
              role="button"
              tabIndex={hiddenCount > 0 && index === 3 ? 0 : -1}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  hiddenCount > 0 &&
                  index === 3
                ) {
                  e.preventDefault();
                  setExpanded(true);
                }
              }}
              onClick={() => {
                if (hiddenCount > 0 && index === 3) {
                  setExpanded(true);
                }
              }}
            >
              <TimelineMedia file={item.file} compact />

              {hiddenCount > 0 && index === 3 ? (
                <VisualOverflowLabel hiddenItems={hiddenItems} />
              ) : null}
            </div>
          );
        })}
      </div>

      {expanded && items.length > 4 ? (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{
            width: "100%",
            marginTop: 8,
            border:
              "1px solid color-mix(in srgb, currentColor 10%, transparent)",
            borderRadius: 12,
            background: "color-mix(in srgb, currentColor 4%, transparent)",
            color: "inherit",
            padding: "0.7rem",
            cursor: "pointer",
            fontWeight: 700,
            opacity: 0.76,
          }}
        >
          {t("showLessVisualMedia")}
        </button>
      ) : null}
    </section>
  );
}

function AudioPlaylist({ items }: { items: TimelinePostItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const t = useTranslations("timelineCollage");
  const format = useFormatter();

  if (items.length === 0) return null;

  const visibleItems = expanded ? items : items.slice(0, 2);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  return (
    <section
      style={{
        marginTop: 10,
        padding: "0.85rem",
        borderRadius: 16,
        background: "color-mix(in srgb, currentColor 3%, transparent)",
        border: "1px solid color-mix(in srgb, currentColor 8%, transparent)",
      }}
    >
      <SectionTitle
        action={
          items.length > 2 ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              style={{
                border: "none",
                background: "transparent",
                color: "inherit",
                opacity: 0.65,
                cursor: "pointer",
                fontSize: "0.78rem",
                fontWeight: 700,
              }}
            >
              {expanded
                ? t("showLess")
                : t("showAll", { count: format.number(items.length) })}
            </button>
          ) : null
        }
      >
        🎧 {t("audioSection")}
      </SectionTitle>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visibleItems.map((item) => (
          <TimelineMedia key={item.id} file={item.file} />
        ))}
      </div>

      {!expanded && hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            width: "100%",
            marginTop: 8,
            border:
              "1px dashed color-mix(in srgb, currentColor 18%, transparent)",
            borderRadius: 12,
            background: "transparent",
            color: "inherit",
            padding: "0.7rem",
            cursor: "pointer",
            opacity: 0.75,
            fontWeight: 700,
          }}
        >
          {t("moreAudioFiles", { count: hiddenCount })}
        </button>
      ) : null}
    </section>
  );
}

function FileList({ items }: { items: TimelinePostItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const t = useTranslations("timelineCollage");
  const format = useFormatter();

  if (items.length === 0) return null;

  const visibleItems = expanded ? items : items.slice(0, 3);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  return (
    <section
      style={{
        marginTop: 10,
        padding: "0.85rem",
        borderRadius: 16,
        background: "color-mix(in srgb, currentColor 3%, transparent)",
        border: "1px solid color-mix(in srgb, currentColor 8%, transparent)",
      }}
    >
      <SectionTitle
        action={
          items.length > 3 ? (
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              style={{
                border: "none",
                background: "transparent",
                color: "inherit",
                opacity: 0.65,
                cursor: "pointer",
                fontSize: "0.78rem",
                fontWeight: 700,
              }}
            >
              {expanded
                ? t("showLess")
                : t("showAll", { count: format.number(items.length) })}
            </button>
          ) : null
        }
      >
        📎 {t("attachmentsSection")}
      </SectionTitle>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visibleItems.map((item) => (
          <TimelineMedia key={item.id} file={item.file} />
        ))}
      </div>

      {!expanded && hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            width: "100%",
            marginTop: 8,
            border:
              "1px dashed color-mix(in srgb, currentColor 18%, transparent)",
            borderRadius: 12,
            background: "transparent",
            color: "inherit",
            padding: "0.7rem",
            cursor: "pointer",
            opacity: 0.75,
            fontWeight: 700,
          }}
        >
          {t("moreFiles", { count: hiddenCount })}
        </button>
      ) : null}
    </section>
  );
}

export default function TimelineCollage({ items }: Props) {
  const t = useTranslations("timelineCollage");
  const groups = useMemo(() => groupItems(items), [items]);

  if (!items.length) return null;

  const mediaTypeCount =
    Number(groups.images.length > 0) +
    Number(groups.videos.length > 0) +
    Number(groups.audios.length > 0) +
    Number(groups.files.length > 0);

  const hasMixedMedia = mediaTypeCount > 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {hasMixedMedia ? (
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 8,
          }}
        >
          <MediaCountPill
            label={t("imagesLabel")}
            count={groups.images.length}
          />
          <MediaCountPill
            label={t("videosLabel")}
            count={groups.videos.length}
          />
          <MediaCountPill
            label={t("audioLabel")}
            count={groups.audios.length}
          />
          <MediaCountPill label={t("filesLabel")} count={groups.files.length} />
        </div>
      ) : null}

      <VisualGrid items={groups.visuals} />

      <AudioPlaylist items={groups.audios} />

      <FileList items={groups.files} />
    </div>
  );
}
